import React, { FC } from 'react';

import { observer } from 'mobx-react';
import { TeamOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { useStores } from '@/components/hooks';
import EditableCellPopup from '@/components/Common/EditableCellPopup';
import useRecipientDetails from '@/components/Common/hooks/useRecipientDetails';
import GroupDetailsModal from '@/components/Common/SharingBlock/GroupDetailsModal';
import type { FilledRecipientEntry } from './types';
import styles from './UsersWithDetailsSection.module.scss';

const PHONE_LAST_DIGITS = -2;

interface UserDetailRowProps {
    email: string;
    showPhones: boolean;
    showPasswords: boolean;
}

const UserDetailRow: FC<UserDetailRowProps> = observer(({ email, showPhones, showPasswords }) => {
    const { currentPhone, passwordInfo, onPhoneSubmit, onPasswordSave } = useRecipientDetails({
        email,
        showPhones,
        showPasswords,
    });

    const hasPhone = !!(currentPhone?.phone && currentPhone?.prefix);
    const hasPassword = !!passwordInfo?.hasPassword;

    return (
        <div className={styles['uwd-row']}>
            <div className={styles['uwd-col-email']}>
                <span className={styles['uwd-email-text']}>{email}</span>
            </div>
            {showPhones && (
                <div className={styles['uwd-col-phone']}>
                    <EditableCellPopup
                        type="phone"
                        phone={currentPhone?.phone}
                        prefix={currentPhone?.prefix}
                        onSave={onPhoneSubmit}
                        isEmpty={!hasPhone}
                        cellTestId={`uwdSection_phone_${email}`}
                        displayValue={
                            hasPhone
                                ? (
                                    <span className={styles['uwd-detail-text']}>
                                        {`+${currentPhone.prefix}*******${currentPhone.phone.slice(PHONE_LAST_DIGITS)}`}
                                    </span>
                                )
                                : <span className={styles['uwd-detail-text-empty']}>—</span>
                        }
                    />
                </div>
            )}
            {showPasswords && (
                <div className={styles['uwd-col-password']}>
                    <EditableCellPopup
                        type="password"
                        maskedPassword={passwordInfo?.maskedPassword}
                        hasPassword={hasPassword}
                        onSave={onPasswordSave}
                        isEmpty={!hasPassword}
                        cellTestId={`uwdSection_password_${email}`}
                        displayValue={
                            hasPassword
                                ? (
                                    <span className={styles['uwd-detail-text']}>
                                        {`•••••••••${(passwordInfo?.maskedPassword || '').slice(PHONE_LAST_DIGITS)}`}
                                    </span>
                                )
                                : <span className={styles['uwd-detail-text-empty']}>—</span>
                        }
                    />
                </div>
            )}
        </div>
    );
});

interface UsersWithDetailsSectionProps {
    filledRecipients: FilledRecipientEntry[];
    showPhones: boolean;
    showPasswords: boolean;
    /**
     * Optional namespace for the section title and column headers.
     * Defaults to 'uploadFiles.policyStep' which has the keys defined.
     */
    titleNameSpace?: string;
    /**
     * Whether to render the GroupDetailsModal alongside the list.
     * Set to false if the parent component already renders the modal.
     */
    renderModal?: boolean;
    /** Extra className on the root wrapper for context-specific padding overrides. */
    className?: string;
    /** Extra className on the header row (e.g. negative margin for edge-to-edge). */
    headerClassName?: string;
    /** Extra className on the list wrapper (e.g. to zero out internal padding). */
    listClassName?: string;
}

const UsersWithDetailsSection: FC<UsersWithDetailsSectionProps> = observer(({
    filledRecipients,
    showPhones,
    showPasswords,
    titleNameSpace = 'uploadFiles.policyStep',
    renderModal = true,
    className,
    headerClassName,
    listClassName,
}) => {
    const { t } = useTranslation();
    const { usersPhonesStore } = useStores();

    if (filledRecipients.length === 0) return null;

    return (
        <>
            <div className={classNames(styles['users-with-details-section'], className)}>
                <div className={classNames(styles['users-with-details-header'], headerClassName)}>
                    <div className={classNames(styles['uwd-col-email'], styles['uwd-col-header'])}>
                        {t(`${titleNameSpace}.usersWithDetails`)}
                    </div>
                    {showPhones && (
                        <div className={classNames(styles['uwd-col-phone'], styles['uwd-col-header'])}>
                            {t(`${titleNameSpace}.phoneNumber`, 'Phone number')}
                        </div>
                    )}
                    {showPasswords && (
                        <div className={classNames(styles['uwd-col-password'], styles['uwd-col-header'])}>
                            {t(`${titleNameSpace}.password`, 'Password')}
                        </div>
                    )}
                </div>
                <div className={classNames(styles['users-with-details-list'], listClassName)}>
                    {filledRecipients.map((entry) => {
                        if (entry.type === 'group') {
                            return (
                                <div
                                    key={entry.groupId}
                                    className={styles['uwd-row-group']}
                                    onClick={() => usersPhonesStore.setPhoneNumbersGroupId(entry.groupId)}
                                >
                                    <div className={styles['uwd-col-email']}>
                                        <TeamOutlined className={styles['uwd-group-icon']} />
                                        <span className={styles['uwd-email-text']}>{entry.groupName}</span>
                                    </div>
                                </div>
                            );
                        }
                        return (
                            <UserDetailRow
                                key={entry.email}
                                email={entry.email}
                                showPhones={showPhones}
                                showPasswords={showPasswords}
                            />
                        );
                    })}
                </div>
            </div>
            {renderModal && usersPhonesStore.phoneNumbersGroupId && (
                <GroupDetailsModal
                    showPhones={showPhones}
                    showPasswords={showPasswords}
                />
            )}
        </>
    );
});

export default UsersWithDetailsSection;
